/**
 * RETIRER DU SERVICE / REMETTRE EN SERVICE — décision du fondateur du
 * 15 octobre 2026, réservée à l'ADMIN (la page ne monte ce composant que si
 * `gesteDeService` rend un geste, et le serveur refuse le tuteur).
 *
 * Tout se passe DANS la page — jamais `confirm()` : le motif d'abord (retrait
 * seulement, obligatoire), puis une confirmation qui dit la conséquence. Le
 * composant n'a pas d'état : la page tient l'étape et le motif, les règles
 * vivent dans `lib/relecture-exercices.ts`.
 */
import {
  MAX_MOTIF_RETRAIT,
  motifRetraitValide,
  type EtapeService,
  type GesteDeService,
} from '../../lib/relecture-exercices';

export interface RetraitDuServiceProps {
  readonly code: string;
  readonly geste: GesteDeService;
  readonly etape: EtapeService;
  readonly motif: string;
  readonly enCours: boolean;
  readonly onOuvrir: () => void;
  readonly onMotif: (m: string) => void;
  readonly onContinuer: () => void;
  readonly onConfirmer: () => void;
  readonly onAnnuler: () => void;
}

export function RetraitDuService(p: RetraitDuServiceProps) {
  const retirer = p.geste === 'retirer';
  if (p.etape === 'ferme') {
    return (
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        disabled={p.enCours}
        onClick={p.onOuvrir}
        data-testid="rx-service-ouvrir"
      >
        {retirer ? 'Retirer du service…' : 'Remettre en service…'}
      </button>
    );
  }
  if (p.etape === 'motif') {
    return (
      <div className="rx-barre__note" data-testid="rx-service-motif">
        <label htmlFor="retrait-motif" className="rx-barre__label">
          Pourquoi le retirer ? Le motif est obligatoire ; il reste attaché à l’exercice et
          s’affiche dans la file. Admin seulement.
        </label>
        <textarea
          id="retrait-motif"
          className="control"
          rows={2}
          maxLength={MAX_MOTIF_RETRAIT}
          value={p.motif}
          onChange={(e) => p.onMotif(e.target.value)}
          placeholder="Ex. : doublon de N1-1-02 ; la bonne réponse est fausse dans la source."
        />
        <div className="rx-barre__ligne">
          <button
            type="button"
            className="btn btn--sm"
            disabled={p.enCours || !motifRetraitValide(p.motif)}
            onClick={p.onContinuer}
          >
            Continuer
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={p.onAnnuler}>
            Annuler
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      className="callout callout--rare rx-service-confirmer"
      role="alertdialog"
      aria-label={retirer ? 'Confirmer le retrait du service' : 'Confirmer la remise en service'}
      data-testid="rx-service-confirmer"
    >
      {retirer ? (
        <p>
          <b>Retirer {p.code} du service ?</b> Les élèves ne le recevront plus ; ses essais passés
          restent lisibles. Motif : « {p.motif.trim()} »
        </p>
      ) : (
        <p>
          <b>Remettre {p.code} en service ?</b> Les élèves le recevront de nouveau, si son chapitre
          est publié.
        </p>
      )}
      <div className="rx-barre__ligne">
        <button
          type="button"
          className="btn btn--sm"
          disabled={p.enCours}
          onClick={p.onConfirmer}
          data-testid="rx-service-confirmer-bouton"
        >
          {retirer ? 'Confirmer le retrait' : 'Confirmer la remise en service'}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={p.onAnnuler}>
          Annuler
        </button>
      </div>
    </div>
  );
}
